import { useState, useEffect } from 'react';
import { BarChart3, Save, Info, LineChart } from 'lucide-react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';

interface AdminGTMSettingsProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

interface AnalyticsSettings {
  gtmEnabled?: boolean;
  gtmContainerId?: string | null;
  ga4Enabled?: boolean;
  ga4MeasurementId?: string | null;
  clarityProjectId?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedAt?: any;
  updatedBy?: string;
}

export function AdminGTMSettings({
  currentUserId,
  showNotify,
}: AdminGTMSettingsProps): JSX.Element {
  const [analyticsSettings, setAnalyticsSettings] = useState<AnalyticsSettings | null>(null);
  const [savingAnalytics, setSavingAnalytics] = useState(false);

  useEffect(() => {
    const analyticsRef = doc(db, 'artifacts', appId, 'settings', 'analytics');
    const unsubscribe = onSnapshot(
      analyticsRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setAnalyticsSettings(docSnap.data() as AnalyticsSettings);
        } else {
          setAnalyticsSettings({
            gtmEnabled: false,
            gtmContainerId: '',
            ga4Enabled: false,
            ga4MeasurementId: '',
            clarityProjectId: '',
          });
        }
      },
      (error) => {
        console.error(
          `[AdminGTMSettings] Error fetching analytics settings: ${error.message}. Falling back to disabled defaults.`
        );
        setAnalyticsSettings({
          gtmEnabled: false,
          gtmContainerId: '',
          ga4Enabled: false,
          ga4MeasurementId: '',
          clarityProjectId: '',
        });
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="text-indigo-600" size={28} />
          <h1 className="text-3xl font-bold text-gray-900">Google Tag Manager</h1>
        </div>
        <p className="text-gray-600">
          Manage GTM container settings from admin without code deployments. Recommended for
          managing Google and third-party tags in one place.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 bg-indigo-50/40">
          <div className="flex items-center gap-2 mb-1">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-100 text-indigo-600">
              <BarChart3 size={14} />
            </span>
            <h2 className="text-base font-bold text-gray-900">Analytics (Google Tag Manager)</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            When enabled, the web app injects your GTM container script at runtime. Changes apply
            after saving and page reload.
          </p>
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!analyticsSettings || savingAnalytics) return;

            const rawContainerId = (analyticsSettings.gtmContainerId ?? '').trim().toUpperCase();
            const gtmEnabled = analyticsSettings.gtmEnabled === true;
            const gtmIdPattern = /^GTM-[A-Z0-9]+$/;

            if (gtmEnabled && !rawContainerId) {
              if (showNotify) {
                showNotify('GTM Container ID is required when GTM is enabled.', 'error');
              }
              return;
            }

            if (rawContainerId && !gtmIdPattern.test(rawContainerId)) {
              if (showNotify) {
                showNotify('Invalid GTM Container ID format. Use "GTM-XXXXXXX".', 'error');
              }
              return;
            }

            const rawGa4Id = (analyticsSettings.ga4MeasurementId ?? '').trim();
            const ga4Enabled = analyticsSettings.ga4Enabled === true;
            const ga4IdPattern = /^G-[A-Z0-9]+$/i;

            if (ga4Enabled && !rawGa4Id) {
              if (showNotify) {
                showNotify('GA4 Measurement ID is required when Firebase/GA4 is enabled.', 'error');
              }
              return;
            }

            if (rawGa4Id && !ga4IdPattern.test(rawGa4Id)) {
              if (showNotify) {
                showNotify('Invalid GA4 Measurement ID format. Use "G-XXXXXXXXX".', 'error');
              }
              return;
            }

            try {
              setSavingAnalytics(true);
              const analyticsRef = doc(db, 'artifacts', appId, 'settings', 'analytics');
              await setDoc(
                analyticsRef,
                {
                  gtmEnabled,
                  gtmContainerId: rawContainerId || null,
                  ga4Enabled,
                  ga4MeasurementId: rawGa4Id || null,
                  clarityProjectId: (analyticsSettings.clarityProjectId ?? '').trim() || null,
                  updatedAt: serverTimestamp(),
                  updatedBy: currentUserId,
                },
                { merge: true }
              );

              if (showNotify) {
                showNotify('Analytics settings saved successfully', 'success');
              }
            } catch (error) {
              console.error(
                `[AdminGTMSettings] Error saving analytics settings: ${
                  error instanceof Error ? error.message : 'Unknown error'
                }`
              );
              if (showNotify) {
                showNotify('Failed to save analytics settings. Please try again.', 'error');
              }
            } finally {
              setSavingAnalytics(false);
            }
          }}
        >
          <div className="px-6 py-5 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Enable GTM</h3>
                <p className="text-xs text-gray-500 mt-1">
                  When enabled, the web app injects your GTM container script at runtime.
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setAnalyticsSettings((prev) => ({
                    ...(prev || {}),
                    gtmEnabled: !(prev?.gtmEnabled === true),
                  }))
                }
                disabled={!analyticsSettings || savingAnalytics}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 shrink-0 ${
                  analyticsSettings?.gtmEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                } ${
                  savingAnalytics ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'
                }`}
                aria-label={`${analyticsSettings?.gtmEnabled ? 'Disable' : 'Enable'} GTM`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                    analyticsSettings?.gtmEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="space-y-1">
              <label htmlFor="gtmContainerId" className="block text-sm font-medium text-gray-700">
                GTM Container ID
              </label>
              <input
                id="gtmContainerId"
                type="text"
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm uppercase"
                placeholder="GTM-XXXXXXX"
                value={analyticsSettings?.gtmContainerId ?? ''}
                onChange={(e) =>
                  setAnalyticsSettings((prev) => ({
                    ...(prev || {}),
                    gtmContainerId: e.target.value.toUpperCase(),
                  }))
                }
              />
              <p className="text-xs text-gray-500">
                Use your container ID from Google Tag Manager (format: GTM-XXXXXXX).
              </p>
            </div>

            <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 text-xs text-indigo-900">
              <p className="font-semibold mb-1 flex items-center gap-1">
                <Info size={14} className="shrink-0" />
                How this works
              </p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>Changes apply immediately after saving and page reload.</li>
                <li>Manage GA4 and third-party tags inside GTM instead of code deployments.</li>
                <li>Recommended for marketing teams managing multiple tags.</li>
              </ul>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-3">
              <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                <Info size={16} className="shrink-0 text-slate-500" />
                Using GTM and GA4 together
              </p>
              <p>
                Yes, both can be active. Whether you should run both depends on how you use them.
              </p>
              <div>
                <p className="font-medium text-slate-800 mb-1">When it&apos;s fine</p>
                <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                  <li>
                    <strong>Different GA4 properties</strong> – e.g. GTM sends to one property,
                    &quot;Firebase / GA4&quot; sends to another (e.g. Firebase default). No
                    duplicate events.
                  </li>
                  <li>
                    <strong>Different roles</strong> – e.g. GTM for ads/other tags only (no GA4 in
                    GTM), and &quot;Firebase / GA4&quot; for the main GA4 property. Again no
                    double-counting.
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-slate-800 mb-1">When it can be a problem</p>
                <p className="text-slate-600">
                  <strong>Same GA4 property</strong> – If the GA4 Measurement ID below is the same
                  property you&apos;re sending to from GTM (e.g. via a GA4 tag in GTM), you&apos;ll
                  get duplicate page views. Counts in Firebase/GA4 will be inflated and data will be
                  harder to interpret.
                </p>
              </div>
              <div>
                <p className="font-medium text-slate-800 mb-1">Practical rule</p>
                <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                  <li>
                    Prefer <strong>one</strong> source of truth per GA4 property: either GTM only
                    (GA4 inside GTM) or direct GA4 only (Firebase/GA4 section), not both pointing
                    at the same property.
                  </li>
                  <li>
                    If you use GTM to manage GA4 and third-party tags, it&apos;s usually best to
                    turn off &quot;Enable GA4&quot; below and run GA4 only through GTM.
                  </li>
                </ul>
              </div>
              <p className="text-slate-600 border-t border-slate-200 pt-2 mt-2">
                So: yes, it&apos;s okay for both to be active, but avoid sending the same events to
                the same GA4 property from both; otherwise you&apos;ll see duplicate data.
              </p>
            </div>
          </div>

          {/* Firebase / Google Analytics (GA4) */}
          <div className="border-t border-gray-200 px-6 pt-6 pb-2 mt-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-100 text-emerald-600">
                <LineChart size={14} />
              </span>
              <h2 className="text-base font-bold text-gray-900">Firebase / Google Analytics (GA4)</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Use the same GA4 property that appears in Firebase Console (Analytics dashboard and
              &quot;View more in Google Analytics&quot;). Enter the Measurement ID from your GA4 web
              stream.
            </p>

            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Enable GA4</h3>
                <p className="text-xs text-gray-500 mt-1">
                  When enabled, the app sends page views to this GA4 property (Firebase default
                  analytics).
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setAnalyticsSettings((prev) => ({
                    ...(prev || {}),
                    ga4Enabled: !(prev?.ga4Enabled === true),
                  }))
                }
                disabled={!analyticsSettings || savingAnalytics}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 shrink-0 ${
                  analyticsSettings?.ga4Enabled ? 'bg-emerald-600' : 'bg-gray-300'
                } ${
                  savingAnalytics ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'
                }`}
                aria-label={`${analyticsSettings?.ga4Enabled ? 'Disable' : 'Enable'} GA4`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                    analyticsSettings?.ga4Enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="space-y-1">
              <label htmlFor="ga4MeasurementId" className="block text-sm font-medium text-gray-700">
                GA4 Measurement ID
              </label>
              <input
                id="ga4MeasurementId"
                type="text"
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                placeholder="G-XXXXXXXXXX"
                value={analyticsSettings?.ga4MeasurementId ?? ''}
                onChange={(e) =>
                  setAnalyticsSettings((prev) => ({
                    ...(prev || {}),
                    ga4MeasurementId: e.target.value.trim(),
                  }))
                }
              />
              <p className="text-xs text-gray-500">
                From Firebase Console (Project settings → your web app) or GA4 → Admin → Data
                streams → your web stream (format: G-XXXXXXXXXX).
              </p>
            </div>
            <div className="space-y-1 mt-4">
              <label htmlFor="clarityProjectId" className="block text-sm font-medium text-gray-700">
                Microsoft Clarity Project ID
              </label>
              <input
                id="clarityProjectId"
                type="text"
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                placeholder="abcd1234"
                value={analyticsSettings?.clarityProjectId ?? ''}
                onChange={(e) =>
                  setAnalyticsSettings((prev) => ({
                    ...(prev || {}),
                    clarityProjectId: e.target.value.trim(),
                  }))
                }
              />
              <p className="text-xs text-gray-500">
                Optional. When set, Clarity is injected once per page load for session replay and heatmaps.
              </p>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end">
            <button
              type="submit"
              disabled={savingAnalytics || !analyticsSettings}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors ${
                savingAnalytics || !analyticsSettings
                  ? 'bg-indigo-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              <Save size={15} className={savingAnalytics ? 'animate-spin' : ''} />
              {savingAnalytics ? 'Saving…' : 'Save Analytics'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
